/**
 * Historical Datings Component
 * Placeholder for Mahabharata, Ramayana, and Buddha dating information
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Clock, Construction } from 'lucide-react';

interface HistoricalDatingsProps {
  type: 'mahabharata' | 'ramayana' | 'buddha';
}

const DATING_INFO = {
  mahabharata: {
    title: 'Mahabharata Dating',
    description: 'Exploring the astronomical date of the Mahabharata War',
    icon: <Clock className="h-5 w-5" />,
    teaser: 'The Mahabharata describes specific planetary positions during the war - conjunction of Saturn and Jupiter, Mars retrograde near Jyeshtha, and a solar eclipse at Kurukshetra. We are building a tool that matches these descriptions against Swiss Ephemeris calculations to explore candidate dates.',
    references: ['Mahabharata (Bhishma Parva, Udyoga Parva)', 'Aryabhata\'s Kali Yuga epoch', 'Vartak (1988), Oak (2011)'],
  },
  ramayana: {
    title: 'Ramayana Dating',
    description: 'Exploring the astronomical date of events in the Ramayana',
    icon: <Clock className="h-5 w-5" />,
    teaser: 'Valmiki\'s Ramayana records planetary positions at key moments - Rama\'s birth (Sun exalted, Jupiter-Moon in Cancer), the departure to the forest, and the return to Ayodhya. We are building a tool to match these against computed ephemeris data.',
    references: ['Valmiki Ramayana (Balakanda, Aranyakanda)', 'Pushkar Bhatnagar (2004)', 'Nilesh Oak (2014)'],
  },
  buddha: {
    title: 'Gautama Buddha Dating',
    description: 'Exploring the astronomical date of the Buddha\'s birth and Mahaparinirvana',
    icon: <Clock className="h-5 w-5" />,
    teaser: 'Buddhist texts record that Siddhartha Gautama was born on Vaishakha Purnima when the Moon was in Vishakha nakshatra. The Mahaparinirvana is also astronomically anchored. We are exploring how these match against sidereal computations for various proposed centuries.',
    references: ['Theravada tradition (544/543 BCE)', 'Ashoka inscriptions', 'Chinese pilgrimage records'],
  },
};

export const HistoricalDatings: React.FC<HistoricalDatingsProps> = ({ type }) => {
  const info = DATING_INFO[type];

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {info.icon}
          {info.title}
        </CardTitle>
        <CardDescription>{info.description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center justify-center py-8 text-center space-y-4">
          <Construction className="h-12 w-12 text-amber-600/40 dark:text-amber-400/40" />
          <Badge variant="secondary" className="text-xs">
            Coming Soon
          </Badge>
          <p className="text-sm text-muted-foreground max-w-lg leading-relaxed">
            {info.teaser}
          </p>
          <div className="pt-2">
            <p className="text-xs font-medium text-muted-foreground/70 mb-1">Key references:</p>
            <p className="text-xs text-muted-foreground/60">
              {info.references.join(' · ')}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default HistoricalDatings;

